"use client";

import { motion } from "framer-motion";

const STEPS = [
  {
    number: "01",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
      </svg>
    ),
    title: "Call comes in",
    description: "Your AI agent picks up every call in under a second — no hold music, no voicemail, no missed opportunity. Works around the clock, including weekends and holidays.",
    detail: "Answers in < 1 second",
    detailColor: "text-violet-400",
  },
  {
    number: "02",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" />
      </svg>
    ),
    title: "Agent qualifies & acts",
    description: "The agent asks the right questions, understands intent, and takes action — booking an appointment, qualifying a lead, routing to a human, or capturing a message.",
    detail: "Trained on your workflows",
    detailColor: "text-cyan-400",
  },
  {
    number: "03",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ),
    title: "Your team follows up",
    description: "Every call ends with a structured summary — caller details, intent, outcome, and next steps — delivered to your inbox or CRM so your team picks up exactly where the agent left off.",
    detail: "Summary to inbox or CRM",
    detailColor: "text-emerald-400",
  },
];

export default function VoiceAgentHowItWorks() {
  return (
    <section className="bg-zinc-900/40 py-24 border-t border-white/[0.04]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="text-center mb-16">
          <p className="text-sm font-semibold text-violet-400 uppercase tracking-widest mb-3">How it works</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            From first ring to closed deal —<br className="hidden sm:block" /> fully automated
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            Three steps. Zero dropped calls. Every interaction captured and handed off cleanly.
          </p>
        </div>

        <div className="relative">
          {/* Connector line (desktop) */}
          <div className="hidden lg:block absolute top-12 left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-transparent via-violet-500/60 to-transparent" />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="relative flex flex-col"
              >
                {/* Step header */}
                <div className="flex items-center gap-4 mb-5">
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 border border-violet-500/20 flex items-center justify-center text-violet-300">
                      {step.icon}
                    </div>
                    <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-zinc-900 border border-white/10 flex items-center justify-center">
                      <span className="text-[9px] font-bold text-zinc-500">{step.number}</span>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                </div>

                {/* Card body */}
                <div className="flex-1 bg-zinc-900/60 rounded-2xl border border-white/[0.06] p-6 hover:border-white/10 transition-colors">
                  <p className="text-zinc-400 text-sm leading-relaxed mb-4">{step.description}</p>
                  <div className={`inline-flex items-center gap-1.5 text-xs font-semibold ${step.detailColor}`}>
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                    </svg>
                    {step.detail}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
